(() => {
  const getAnswer = (item) => item.querySelector('[data-pilates-faq-answer]');

  const setAnswerHeight = (item, height) => {
    const answer = getAnswer(item);
    if (answer) answer.style.height = height;
  };

  const finishAnimation = (item, isOpen) => {
    item.pilatesFaqTimer = null;
    item.classList.remove('is-animating');
    item.dataset.animating = 'false';

    if (isOpen) {
      setAnswerHeight(item, 'auto');
      return;
    }

    item.open = false;
    setAnswerHeight(item, '0px');
  };

  const openItem = (item) => {
    const answer = getAnswer(item);
    if (!answer) {
      item.open = true;
      return;
    }

    window.clearTimeout(item.pilatesFaqTimer);

    if (item.open && item.dataset.animating !== 'true') return;

    item.open = true;
    item.classList.add('is-animating');
    item.dataset.animating = 'true';
    answer.style.height = '0px';

    window.requestAnimationFrame(() => {
      answer.style.height = `${answer.scrollHeight}px`;
    });

    item.pilatesFaqTimer = window.setTimeout(() => finishAnimation(item, true), 340);
  };

  const closeItem = (item) => {
    const answer = getAnswer(item);
    if (!item.open || !answer) {
      item.open = false;
      return;
    }

    window.clearTimeout(item.pilatesFaqTimer);

    item.classList.add('is-animating');
    item.dataset.animating = 'true';
    answer.style.height = `${answer.scrollHeight}px`;

    window.requestAnimationFrame(() => {
      answer.style.height = '0px';
    });

    item.pilatesFaqTimer = window.setTimeout(() => finishAnimation(item, false), 340);
  };

  // ── Modal helpers ──────────────────────────────────────────────────────────

  const openModal = (modal) => {
    modal.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    modal.querySelector('[data-pfaq-modal-close]')?.focus();
  };

  const closeModal = (modal) => {
    modal.classList.remove('is-open');
    document.body.style.overflow = '';
    // return focus to the trigger that opened it
    if (modal._triggerEl) {
      modal._triggerEl.focus();
      modal._triggerEl = null;
    }
  };

  const initModals = (scope) => {
    scope.querySelectorAll('[data-pfaq-modal]').forEach((modal) => {
      // move to body to escape any parent stacking context
      if (modal.parentElement !== document.body) {
        document.body.appendChild(modal);
      }

      // close button
      modal.querySelector('[data-pfaq-modal-close]')?.addEventListener('click', () => closeModal(modal));

      // click on backdrop (overlay itself, not the inner card)
      modal.addEventListener('click', (e) => {
        if (e.target === modal) closeModal(modal);
      });
    });

    // Esc key — delegated to document once
    if (!document._pfaqEscBound) {
      document._pfaqEscBound = true;
      document.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape') return;
        document.querySelectorAll('[data-pfaq-modal].is-open').forEach(closeModal);
      });
    }

    // trigger spans
    scope.querySelectorAll('[data-pfaq-modal-trigger]').forEach((trigger) => {
      trigger.addEventListener('click', (e) => {
        e.stopPropagation();
        const targetId = trigger.dataset.pfaqModalTrigger;
        const modal = document.getElementById(targetId);
        if (!modal) return;
        modal._triggerEl = trigger;
        openModal(modal);
      });
    });
  };

  // ── FAQ accordion ──────────────────────────────────────────────────────────

  const initPilatesFaq = (root) => {
    if (root.dataset.pilatesFaqReady === 'true') return;

    const items = Array.from(root.querySelectorAll('[data-pilates-faq-item]'));
    const toggle = root.querySelector('[data-pilates-faq-toggle]');

    if (items.length === 0) return;

    root.dataset.pilatesFaqReady = 'true';

    items.forEach((item) => {
      const answer = getAnswer(item);
      if (answer) answer.style.height = item.open ? 'auto' : '0px';

      // Prevent native <details> toggle on summary click; actual toggle is on the item
      item.querySelector('summary')?.addEventListener('click', (event) => {
        event.preventDefault();
      });

      item.addEventListener('click', (event) => {
        // Don't toggle when clicking inside the answer area (links, modal triggers, etc.)
        if (answer && answer.contains(event.target)) return;

        if (item.open) {
          closeItem(item);
          return;
        }

        items.forEach((otherItem) => {
          if (otherItem !== item) closeItem(otherItem);
        });

        openItem(item);
      });
    });

    // on mobile: expand all items and set toggle to expanded state
    if (window.matchMedia('(max-width: 1024px)').matches) {
      items.forEach((item) => {
        item.hidden = false;
        item.classList.remove('is-hidden');
      });
      if (toggle) {
        const showLessText = toggle.dataset.showLessText || 'Show Less';
        toggle.setAttribute('aria-expanded', 'true');
        toggle.setAttribute('aria-label', showLessText);
      }
    }

    toggle?.addEventListener('click', () => {
      const isExpanded = toggle.getAttribute('aria-expanded') === 'true';
      const showMoreText = toggle.dataset.showMoreText || 'Show More';
      const showLessText = toggle.dataset.showLessText || 'Show Less';
      const visibleLimit = parseInt(root.dataset.initialVisibleCount || '3', 10);

      items.forEach((item, index) => {
        if (index < visibleLimit) return;

        if (isExpanded) {
          closeItem(item);
          item.hidden = true;
          item.classList.add('is-hidden');
          return;
        }

        item.hidden = false;
        item.classList.remove('is-hidden');
      });

      toggle.setAttribute('aria-expanded', isExpanded ? 'false' : 'true');
      toggle.setAttribute('aria-label', isExpanded ? showMoreText : showLessText);
      toggle.blur();
    });

    initModals(root);
  };

  const initAll = (scope = document) => {
    scope.querySelectorAll('[data-pilates-faq]').forEach(initPilatesFaq);
  };

  initAll();
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
